/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { clamp } from 'lodash';
import { EDGE_PADDING } from '../../../WarningsTimeline/warningsTimelineUtils';

export const SNAP_MINUTES = 30;
export const MIN_DURATION_MINUTES = SNAP_MINUTES;
export const MIN_VISIBLE_MINUTES = SNAP_MINUTES;
export const ABSOLUTE_MAX_DAY = 7;
export const EDGE_HIT_ZONE_PX = 8;
export const EXPANDED_BLOCK_TOP = 58;
export const EXPANDED_BLOCK_HEIGHT = 56;
export const TIME_FIELD_WIDTH = 200;
export const TIME_FIELD_GAP = 8;

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const SNAP_MS = SNAP_MINUTES * MS_PER_MINUTE;
const MIN_DURATION_MS = MIN_DURATION_MINUTES * MS_PER_MINUTE;
const MIN_VISIBLE_MS = MIN_VISIBLE_MINUTES * MS_PER_MINUTE;

export type DragMode = 'start' | 'end' | 'move';

export const snapToInterval = (
  timeMs: number,
  intervalMs: number = SNAP_MS,
): number => Math.round(timeMs / intervalMs) * intervalMs;

// under a day the overflow shows hours; from a day on it shows whole days, like the day headers
export const formatOverflowHours = (overflowMs: number): string => {
  const hours = overflowMs / MS_PER_HOUR;
  if (hours >= 24) {
    return `${Math.round(hours / 24)}`;
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded}h`;
};

// editing the start time in the input keeps the warning duration, so the end time
// moves along; it is shortened when that would pass maxUntilMs
export const getEndTimeForMovedStart = (
  fromMs: number,
  untilMs: number,
  newFromMs: number,
  maxUntilMs: number,
): number => Math.min(newFromMs + (untilMs - fromMs), maxUntilMs);

export interface TimeFieldPositions {
  startLeft: number;
  endLeft: number;
  isStacked: boolean;
}

// Fields center on block edges (clamped to axis ticks) and stack vertically only when they would touch.
export const getTimeFieldPositions = ({
  left,
  width,
  containerWidth,
}: {
  left: number;
  width: number;
  containerWidth: number;
}): TimeFieldPositions => {
  const minLeft = EDGE_PADDING;
  const maxLeft = Math.max(
    minLeft,
    containerWidth - EDGE_PADDING - TIME_FIELD_WIDTH,
  );
  const startLeft = clamp(left - TIME_FIELD_WIDTH / 2, minLeft, maxLeft);
  const endLeft = clamp(left + width - TIME_FIELD_WIDTH / 2, minLeft, maxLeft);
  if (endLeft >= startLeft + TIME_FIELD_WIDTH + TIME_FIELD_GAP) {
    return { startLeft, endLeft, isStacked: false };
  }
  const stackedLeft = clamp(
    left + width / 2 - TIME_FIELD_WIDTH / 2,
    minLeft,
    maxLeft,
  );
  return { startLeft: stackedLeft, endLeft: stackedLeft, isStacked: true };
};

export interface ComputeDraggedTimesParams {
  mode: DragMode;
  deltaMs: number;
  fromMs: number;
  untilMs: number;
  rangeStartMs: number;
  rangeEndMs: number;
  nowMs: number;
  maxUntilMs: number;
}

const ceilSnap = (timeMs: number): number =>
  Math.ceil(timeMs / SNAP_MS) * SNAP_MS;

// Dragging may extend past the range edges (at least MIN_VISIBLE_MS stays visible),
// but the end always stays between now and maxUntilMs.
export const computeDraggedTimes = ({
  mode,
  deltaMs,
  fromMs,
  untilMs,
  rangeStartMs,
  rangeEndMs,
  nowMs,
  maxUntilMs,
}: ComputeDraggedTimesParams): { fromMs: number; untilMs: number } => {
  if (mode === 'start') {
    const newFrom = Math.min(
      snapToInterval(fromMs + deltaMs),
      untilMs - MIN_DURATION_MS,
      rangeEndMs - MIN_VISIBLE_MS,
    );
    return { fromMs: newFrom, untilMs };
  }
  if (mode === 'end') {
    const newUntil = Math.max(
      Math.min(snapToInterval(untilMs + deltaMs), maxUntilMs),
      fromMs + MIN_DURATION_MS,
      rangeStartMs + MIN_VISIBLE_MS,
      ceilSnap(nowMs),
    );
    return { fromMs, untilMs: newUntil };
  }
  const duration = untilMs - fromMs;
  const newFrom = clamp(
    snapToInterval(fromMs + deltaMs),
    Math.max(
      rangeStartMs + MIN_VISIBLE_MS - duration,
      ceilSnap(nowMs) - duration,
    ),
    Math.min(rangeEndMs - MIN_VISIBLE_MS, maxUntilMs - duration),
  );
  return { fromMs: newFrom, untilMs: newFrom + duration };
};
